import { useMemo } from "react"
import { getLegislationDiff } from "../amends/lib/constants/legislation-amends-keys.mjs"
import { Box, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { CompareButtonsBelow } from "slot-definitions"
import { findAuthoringTask } from "./lib/authoring-task-lookup"
import { InlineCompareButtons } from "routes/schedule/compare/inline-approval-buttons/inline-compare-buttons"
import { InlineRejectionEditor } from "./inline-rejection-editor"
import { If } from "lib/@components/switch"
import { AmendsChangeType } from "authoring/lib/enum/amends-change-type.mjs"
import { AuthoringReviewCompareTask } from "./authoring-review-compare-task"
import { AuthoringReviewMissingTask } from "./authoring-review-missing-task"
import { AuthoringReviewSideBySideDetailsRow } from "./authoring-review-side-by-side-details-row"
import { AuthoringReviewSideBySideIntroRow } from "./authoring-review-side-by-side-intro-row"
import { AuthoringReviewAmendsBlock } from "./authoring-review-amends-block"
import { AuthoringReviewPriorRejection } from "./authoring-review-prior-rejection"
import { AuthoringReviewLegislationRow } from "./authoring-review-legislation-row"
import { useParams } from "react-router"
import { getScheduleEditorLegislation } from "./controller/get-schedule-editor-legislation"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { refreshOnlyFor } from "lib/graphql/api"
import { createDraftId } from "authoring/routes/project/lib/project-utils"

export function AuthoringReviewBothPanel() {
    CompareButtonsBelow.usePlug(<InlineRejectionEditor />)

    const { target, comparisonTarget, comparisons, compare } = useBoundContext()
    const { projectId, scheduleId } = useParams()
    const draftId = createDraftId(projectId, scheduleId)
    const movements = comparisons?.find((item) => item?.props?.children?.props?.componentType === "Movements") ?? null
    const missingTasks = comparisonTarget.tasks.filter((task) => !findAuthoringTask(compare.tasks, task))
    const groupReviewId = target?.groupReviewId
    const legislationChanges = useMemo(computeLegislationChanges, [compare, comparisonTarget])
    const legislationIds = useMemo(collectLegislationIds, [legislationChanges])
    const legislationList = getScheduleEditorLegislation.useResults(
        { legacyIds: legislationIds, projectId },
        refreshOnlyFor([draftId, ...legislationIds]),
        withInitialValue([])
    )
    const legislationLookup = useMemo(buildLegislationLookup, [legislationList])

    return (
        <Bound
            editInDialog={true}
            acceptId="movements"
            groupReviewId={groupReviewId}
            legislationLookup={legislationLookup}
        >
            <AuthoringReviewSideBySideDetailsRow
                field="title"
                label="Title"
                acceptId="details"
                idFromGroup="details"
                amendsKey="title"
                subtitle="Change Schedule Title Reason"
            />
            <AuthoringReviewSideBySideDetailsRow
                field="code"
                label="Code"
                acceptId="details:code"
                idFromGroup="details:code"
                amendsKey="code"
                subtitle="Change Schedule Code Reason"
            />
            <AuthoringReviewSideBySideDetailsRow
                field="unitOfMeasure"
                label="Unit of Measure"
                acceptId="details:unitOfMeasure"
                idFromGroup="details:unitOfMeasure"
                amendsKey="unitOfMeasure"
                subtitle="Change Unit of Measure Reason"
            />
            <AuthoringReviewSideBySideIntroRow
                field="content"
                label="Introduction"
                idFromGroup="intro.content"
                amendsKey="intro.content"
                subtitle="Change Introduction Reason"
                dataCy="side-by-side-intro-content"
            />
            <AuthoringReviewSideBySideIntroRow
                field="note"
                label="Notes"
                idFromGroup="intro.note"
                amendsKey="intro.note"
                subtitle="Change Note Reason"
                dataCy="side-by-side-intro-note"
            />
            <Repeat list={compare.tasks} pass="task" item={<AuthoringReviewCompareTask />} />
            <Repeat list={missingTasks} pass="task" item={<AuthoringReviewMissingTask />} />
            {movements}
            <If value={movements}>
                <AuthoringReviewPriorRejection amendsKey="movements" />
                <AuthoringReviewAmendsBlock
                    amendsKey="movements"
                    subtitle="Change Task Order Reason"
                    cyPrefix="side-by-side-amends"
                />
                <InlineCompareButtons idFromGroup={"movements"} to={target} from={comparisonTarget} />
            </If>
            <If value={legislationChanges.length}>
                <Typography sx={{ pl: 5, mt: 2 }} component="div" variant="subtitle1">
                    References
                </Typography>
                {/*
                    Stable key per legislationId — without this the default getKey() falls
                    into a WeakMap keyed by object reference, and the recomputed change
                    objects after every draft-data refetch produce fresh keys, so React
                    unmounts and remounts each row. That destroyed the inline-compare
                    button's local `accepted` state and produced the Approved → Approve →
                    Approved flicker reported on FIQ-1511.
                */}
                <Repeat
                    list={legislationChanges}
                    item={<AuthoringReviewLegislationRow />}
                    keyFn={(item) => item.legislationId}
                />
            </If>
            <Box mb={60} />
        </Bound>
    )

    function computeLegislationChanges() {
        const diff = getLegislationDiff(comparisonTarget?.legislation, compare?.legislation)
        const changes = []
        for (const id of diff.added) {
            changes.push({ legislationId: id, changeType: AmendsChangeType.ADDED })
        }
        for (const id of diff.removed) {
            changes.push({ legislationId: id, changeType: AmendsChangeType.REMOVED })
        }
        return changes
    }

    function collectLegislationIds() {
        return [...new Set(legislationChanges.map((change) => String(change.legislationId)))]
    }

    function buildLegislationLookup() {
        const lookup = new Map()
        for (const record of legislationList) lookup.set(String(record.legacyId), record)
        return lookup
    }
}
